import { Route } from "@/routes/__root";
import {
  useClient,
  useDeleteAllTransactionOrder,
  useDraftOrdersNotifyClient,
  useInfiniteTransactionOrders,
} from "creso-core/hooks";
import Amount from "@funds/components/atoms/amount";
import { useRouter, useSearch } from "@tanstack/react-router";
import { ScrollArea, Button, V2Loader, cn } from "web-design-system";
import { useCallback, useContext, useRef } from "react";
import { toast } from "sonner";
import { Avatar } from "web-core";
import { getInitials } from "creso-core/utils";
import { handleError } from "@/utils/error";
import { RefreshCcwIcon, SidebarIcon, Trash2Icon } from "lucide-react";
import { APIError } from "web-core/utils";
import OrderItemComponent from "./order-item";
import { TransactionContext } from "./transaction-wizard";

export default function TransactionOrdersCart() {
  const { client_id = 0, action } = useSearch({
    from: Route.id,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const {
    investment_account_guid = "",
    updateContext,
    selectedOrder,
  } = useContext(TransactionContext);

  const {
    orders,
    isFetching,
    isRefetching,
    total = 0,
    isPlaceholderData,
    refetch,
  } = useInfiniteTransactionOrders(investment_account_guid);

  const { mutateAsync: notifyClient, isPending: isNotifyingClient } =
    useDraftOrdersNotifyClient(investment_account_guid);

  const { data: client } = useClient(client_id);

  const { mutate: deleteAll, isPending: isDeletingAll } =
    useDeleteAllTransactionOrder(investment_account_guid);

  const notifyClients = useCallback(async () => {
    try {
      await notifyClient();
      toast.success("Notification sent to client");
    } catch (error) {
      if (error instanceof APIError) {
        if (error.data.code === "MISSING_EUIN") {
          toast.error("Unable to notify client", {
            description: (
              <span>
                EUIN not found. Please update your EUIN{" "}
                <a
                  href="/account/setting"
                  className="underline text-primary font-semibold"
                >
                  here
                </a>
              </span>
            ),
          });
        } else {
          handleError(error, "Unable to notify client");
        }
      } else {
        handleError(error, "Unable to notify client");
      }
    }
  }, [notifyClient]);

  const router = useRouter();

  const handleSidebarToggle = useCallback(() => {
    if (action === "view") {
      const isSelectedOrderInList =
        !!selectedOrder &&
        !!orders?.some((order) => order.id === selectedOrder.id);

      if (
        orders &&
        orders.length > 0 &&
        (!selectedOrder || !isSelectedOrderInList)
      ) {
        // Select the first order so the form (e.g. SIP form) shows correct content
        updateContext?.({ selectedOrder: orders[0] });
      } else {
        router.navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            action: "edit",
          }),
          replace: true,
        });
      }
    } else {
      router.navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          action: "view",
        }),
        replace: true,
      });
    }
  }, [action, selectedOrder, orders, updateContext, router]);

  return (
    <div
      className={cn(
        "flex flex-col w-80 h-full bg-card rounded-l-lg",
        action === "view" && "w-100 rounded-lg"
      )}
      ref={containerRef}
    >
      <ScrollArea className="flex-1 py-4 px-3">
        <V2Loader isLoading={!client}>
          <div className="flex flex-col gap-4 flex-1">
            {client ? (
              <div className="flex flex-row h-13 sticky top-0 bg-card z-10 items-center gap-3">
                <Avatar
                  url={""}
                  fallback={getInitials(client.name)}
                  size="lg"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{client.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {client?.pan}
                  </span>
                </div>
                <Button
                  onClick={handleSidebarToggle}
                  variant="ghost"
                  size="icon"
                  className={cn("ml-auto", action !== "view" && "bg-secondary")}
                >
                  <SidebarIcon />
                </Button>
              </div>
            ) : null}
            {orders?.length ? (
              <div className="flex flex-col flex-1 gap-1">
                <div className="flex flex-row items-center justify-between">
                  <span className="font-semibold text-base">Order list</span>
                  <Button
                    size="icon"
                    title="Refetch orders"
                    variant="ghost"
                    className="ml-auto"
                    disabled={isFetching}
                    onClick={() =>
                      refetch()
                        .then(() => {
                          toast.success("Refreshed orders successfully");
                        })
                        .catch((error) => {
                          handleError(error, "Unable to refresh orders");
                        })
                    }
                  >
                    <RefreshCcwIcon
                      className={cn("size-4", isRefetching && "animate-spin")}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Clear all orders"
                    className="ml-1"
                    onClick={() => {
                      // Only clear selectedOrder if it's an existing order being edited
                      // Don't clear if user is creating a new order (no selectedOrder.id)
                      if (selectedOrder?.id) {
                        updateContext?.({
                          selectedOrder: null,
                        });
                      }
                      deleteAll();
                    }}
                  >
                    <Trash2Icon />
                  </Button>
                </div>
                <div
                  className={cn(
                    "flex flex-col gap-px",
                    isPlaceholderData && "opacity-50"
                  )}
                >
                  {orders.map((d) => {
                    return <OrderItemComponent key={d.id} order={d} />;
                  })}
                </div>
                <div className="flex flex-col gap-2 z-10 pt-2 mt-auto sticky bg-card bottom-0">
                  <div className="flex flex-row gap-1 items-center mx-2">
                    <span className="text-xs font-medium">Amount payable</span>
                    <span className="text-sm font-medium ml-auto">
                      <Amount>{total}</Amount>
                    </span>
                  </div>
                  <Button
                    className="w-full mt-2"
                    size="lg"
                    type="submit"
                    onClick={notifyClients}
                    disabled={isNotifyingClient}
                  >
                    Notify client
                  </Button>
                </div>
              </div>
            ) : null}
            {!orders?.length && !isFetching && !isDeletingAll ? (
              <div className="size-full text-sm flex-1 flex justify-center items-center text-muted-foreground">
                Order list empty
              </div>
            ) : null}
            {!orders?.length && isFetching ? (
              <V2Loader className="size-full" isLoading={true} />
            ) : null}
          </div>
        </V2Loader>
      </ScrollArea>
    </div>
  );
}
